import { ArrowLeft } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { useAdminResource } from '../useAdminResource'
import { money, formatDate, formatDateTime } from '../format'
import { AdminCancelPanel } from '../components/AdminCancelPanel'
import type { AdminOrderDetail as Detail } from '../types'

const grams = (value: number) => value ? `${value} g` : '—'
const mm = (value: number) => value ? `${value} mm` : '—'

function Snapshot({ title, json }: { title: string; json: string }) {
  let pretty = json
  try { pretty = JSON.stringify(JSON.parse(json), null, 2) } catch { /* show as stored */ }
  if (!pretty || pretty === '{}') return null
  return (
    <details className="admin-snapshot">
      <summary>{title}</summary>
      <pre>{pretty}</pre>
    </details>
  )
}

export function AdminOrderDetail() {
  const { orderID = '' } = useParams()
  const { data: detail, error, busy, reload: load } = useAdminResource<Detail>(`/api/admin/orders/${encodeURIComponent(orderID)}`)

  // Only block the page on the first load. Refreshing after an action must
  // not unmount the panel that is showing the operator what it recorded.
  if (busy && !detail) return <section className="page"><p className="admin-empty">Loading order…</p></section>
  if (error || !detail) return (
    <section className="page">
      <Link to="/admin/orders" className="back-link"><ArrowLeft size={16} />All orders</Link>
      <div className="form-error" role="alert">{error || 'Order not found.'}</div>
    </section>
  )

  const { order, customer, package: pkg } = detail
  const showCancellationFirst = order.cancellationPending || order.status === 'cancelled'
  const margin = order.grossMinor - order.carrierCostMinor - order.refundedMinor
  const steps: [string, string|undefined][] = [
    ['Created', order.createdAt],
    ['Forwarding authorized', order.authorizedAt],
    ['Arrived at warehouse', order.receivedAt],
    ['Shipped', order.shippedAt],
  ]

  return (
    <section className="admin-detail page page--staff">
      <Link to="/admin/orders" className="back-link"><ArrowLeft size={16} />All orders</Link>

      <header className="admin-detail__head">
        <div>
          <div className="eyebrow"><span aria-hidden="true" />{order.status.replace(/_/g, ' ')}</div>
          <h1>{order.publicId}</h1>
          <p>{order.contentsSummary} to {[order.destinationCity, order.destinationCountry].filter(Boolean).join(', ')}
            {order.retailer && <> · from {order.retailer}</>}</p>
        </div>
        {order.rxid && <div className="admin-detail__rxid"><small>RXID</small><b>{order.rxid}</b></div>}
      </header>
      {order.archivedAt && <p className="admin-notice">Archived history — not in the active queue.</p>}
      {order.cancellationPending && <p className="admin-notice">Cancellation pending. This order is on hold; verify the refund below to finish.</p>}
      {showCancellationFirst && <AdminCancelPanel key={order.id} orderID={order.id} onDone={load} />}

      <ol className="admin-timeline">
        {steps.map(([label, at]) => (
          <li key={label} className={at ? 'done' : ''}>
            <span>{label}</span>
            <b>{at ? formatDateTime(at) : 'Not yet'}</b>
          </li>
        ))}
      </ol>

      <div className="admin-detail__grid">
        <article className="admin-card">
          <h2>Money</h2>
          <dl className="admin-money">
            <div><dt>Postage quoted</dt><dd>{money(order.postageMinor, order.currency)}</dd></div>
            <div><dt>Routing fee</dt><dd>{money(order.serviceFeeMinor, order.currency)}</dd></div>
            <div><dt>Processing fee</dt><dd>{money(order.paymentFeeMinor, order.currency)}</dd></div>
            <div className="admin-money__total"><dt>Live charges, both legs</dt><dd>{money(order.grossMinor, order.currency)}</dd></div>
            <div><dt>Carrier charged us</dt><dd>{order.carrierCostMinor ? money(order.carrierCostMinor, order.currency) : '—'}</dd></div>
            {order.refundedMinor > 0 && (
              <div><dt>Refunded to customer</dt><dd className="admin-neg">−{money(order.refundedMinor, order.currency)}</dd></div>
            )}
            <div className="admin-money__total">
              <dt>After postage &amp; refunds</dt>
              <dd className={margin < 0 ? 'admin-neg' : undefined}>
                {money(margin, order.currency)}
              </dd>
            </div>
          </dl>
          <p className="admin-card__empty">USD. Before actual processing costs. Unpurchased postage is not deducted. Test payments and staff authorizations are not cash receipts.</p>
        </article>

        <article className="admin-card">
          <h2>Customer</h2>
          <dl className="admin-money">
            <div><dt>Name</dt><dd><Link to={`/admin/customers/${customer.userId}`}>{customer.fullName || '—'}</Link></dd></div>
            <div><dt>Email</dt><dd>{customer.email}</dd></div>
            <div><dt>Account since</dt><dd>{formatDate(customer.createdAt)}</dd></div>
            <div><dt>Orders placed</dt><dd>{customer.orderCount}</dd></div>
            <div className="admin-money__total"><dt>Lifetime charges</dt><dd>{money(customer.lifetimeGrossMinor)}</dd></div>
          </dl>
        </article>

        <article className="admin-card">
          <h2>Package</h2>
          {pkg ? (
            <dl className="admin-money">
              <div><dt>Status</dt><dd>{pkg.status.replace(/_/g, ' ')}</dd></div>
              <div><dt>Match</dt><dd>{pkg.matchStatus.replace(/_/g, ' ')}</dd></div>
              <div><dt>Inbound tracking</dt><dd>{pkg.inboundTrackingCode || '—'}</dd></div>
              <div><dt>Weighed</dt><dd>{grams(pkg.actualWeightGrams)}</dd></div>
              <div><dt>Measured</dt><dd>{pkg.lengthMm ? `${mm(pkg.lengthMm)} × ${mm(pkg.widthMm)} × ${mm(pkg.heightMm)}` : '—'}</dd></div>
              <div><dt>Verified</dt><dd>{pkg.verifiedAt ? formatDateTime(pkg.verifiedAt) : 'Not verified'}</dd></div>
              {pkg.inspectionNotes && <div><dt>Notes</dt><dd>{pkg.inspectionNotes}</dd></div>}
            </dl>
          ) : <p className="admin-card__empty">Nothing has arrived for this order yet.</p>}
        </article>
      </div>

      <article className="admin-card">
        <h2>Payments</h2>
        {detail.payments.length ? (
          <div className="admin-table-wrap admin-table-wrap--flush">
            <table className="admin-table admin-table--compact">
              <thead><tr><th>State</th><th>Provider</th><th>Transaction</th><th className="num">Amount</th><th>Created</th></tr></thead>
              <tbody>{detail.payments.map(p => (
                <tr key={p.id}>
                  <td>{p.state}<small>{p.leg === 'inbound' ? 'Warehouse → RX Routing' : 'RX Routing → destination'}</small>{(p.errorCode || p.errorMessage) && <small className="admin-neg">{[p.errorCode, p.errorMessage].filter(Boolean).join(': ')}</small>}</td><td>{p.provider}{p.environment && ` (${p.environment})`}</td>
                  <td><code>{p.providerTransactionId || '—'}</code></td>
                  <td className="num">{money(p.expectedAmountMinor, p.currency)}</td>
                  <td>{formatDateTime(p.createdAt)}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        ) : <p className="admin-card__empty">No charge has been attempted.</p>}
      </article>

      {detail.refunds.length > 0 && (
        <article className="admin-card">
          <h2>Refunds</h2>
          <div className="admin-table-wrap admin-table-wrap--flush">
            <table className="admin-table admin-table--compact">
              <thead><tr><th>State</th><th>Reason</th><th>Provider ref</th><th className="num">Amount</th><th>Created</th></tr></thead>
              <tbody>{detail.refunds.map(r => (
                <tr key={r.id}>
                  <td>{r.state}</td><td>{r.reason || '—'}</td>
                  <td><code>{r.providerRefundId || '—'}</code></td>
                  <td className="num">{money(r.amountMinor, r.currency)}</td>
                  <td>{formatDateTime(r.createdAt)}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </article>
      )}

      <article className="admin-card">
        <h2>Labels</h2>
        {detail.labels.length ? (
          <div className="admin-table-wrap admin-table-wrap--flush">
            <table className="admin-table admin-table--compact">
              <thead><tr><th>State</th><th>Carrier</th><th>Tracking</th><th className="num">Cost</th><th>Purchased</th></tr></thead>
              <tbody>{detail.labels.map(l => (
                <tr key={l.id}>
                  <td>{l.state}{l.refundState && ` · refund ${l.refundState}`}<small>{l.leg === 'inbound' ? 'Warehouse → RX Routing' : 'RX Routing → destination'}</small>{l.errorMessage && <small className="admin-neg">{l.errorMessage}</small>}</td>
                  <td>{[l.carrier, l.service].filter(Boolean).join(' ') || '—'}</td>
                  <td><code>{l.trackingCode || '—'}</code>{l.trackingStatus && <small>{l.trackingStatus}</small>}</td>
                  <td className="num">{l.costMinor ? money(l.costMinor, l.currency || 'USD') : '—'}</td>
                  <td>{l.purchasedAt ? formatDateTime(l.purchasedAt) : '—'}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        ) : <p className="admin-card__empty">No label has been bought.</p>}
      </article>

      {detail.audit.length > 0 && (
        <article className="admin-card">
          <h2>Audit trail</h2>
          <ul className="admin-audit">
            {detail.audit.map((entry, index) => (
              <li key={index}>
                <b>{entry.action}</b>
                <span>{entry.actorType}{entry.actorId && ` · ${entry.actorId.slice(0, 8)}`}</span>
                <time>{formatDateTime(entry.createdAt)}</time>
              </li>
            ))}
          </ul>
        </article>
      )}

      {!showCancellationFirst && <AdminCancelPanel key={order.id} orderID={order.id} onDone={load} />}

      <article className="admin-card">
        <h2>Snapshots</h2>
        <p className="admin-card__empty">The order’s recorded address, parcel and customs details.</p>
        <Snapshot title="Destination" json={detail.destination} />
        <Snapshot title="Parcel" json={detail.parcel} />
        <Snapshot title="Customs" json={detail.customs} />
      </article>
    </section>
  )
}
